import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useContext,
  useLayoutEffect,
  useMemo
} from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { List, Skeleton, message } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { ReactComponent as CommentSvg } from '@assets/icons/comment.svg';
import { ReactComponent as CommentCloseSvg } from '@assets/icons/comment-close.svg';
import { ReactComponent as EmptySearchSvg } from '@assets/icons/empty-search.svg';
import { ReactComponent as WhiteArrowSvg } from '@assets/icons/arrow-right-without-line.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as KeyboardHideSvg } from '@assets/icons/keyboard-hide.svg';
import { ReactComponent as CopySvg } from '@assets/icons/copy-secondary.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { ReactComponent as ClipboardSvg } from '@assets/icons/clipboard.svg';
import { ReactComponent as HelpCircleSvg } from '@assets/icons/help-circle.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import classNames from 'classnames';
import moment from 'moment';
import CommentEditor from '@pages/MediaViewer/Comments/components/CommentEditor';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';
import {
  formatCommentsTree,
  TCommentItem
} from '@pages/MediaViewer/Comments/utils';
import CommentTask from '@pages/MediaViewer/Comments/components/CommentTask';
import {
  AssetItemDto,
  AssetVersionCommentDto,
  AssetVersionItemDto
} from '@api/Api';
import {
  createNewComment,
  setCopiedComments
} from '@redux/actions/mediaViewerAction';
import { usePasteAssetCommentsMutation } from '@hooks/queries/asset';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import PanelSection from '@pages/MediaViewer/components/PanelSection';
import {
  DEFAULT_DRAW_TOOL,
  isAnnotationDrawTool
} from '@pages/MediaViewer/helpers/drawToolShape';
import { getFloatCanvasAnnotations } from '@pages/MediaViewer/components/FloatCommentItem/floatCommentHelpers';
import {
  useAssetMVPermissions,
  useSelectedMVComment,
  useTypedSelector
} from '@hooks';
import { toggleSingInExternalReviewerModal } from '@redux/actions/modalAction';
import './Comments.less';
import { useAuth } from '@hooks/useAuth';
import { nonNull } from '@helpers/non-null';
import {
  exportAsCSV,
  exportAsPlainText,
  exportAsXML
} from '@helpers/exportComments';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { useOrganization } from '@components/OrganizationBoundary';
import {
  setAssetListNeedRefresh,
  setListsNeedRefreshInCampaign
} from '@redux/reducers/tasks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useCreateTask } from '@hooks/tasks';
import { useFetch } from '@hooks/useFetch';
import {
  assetStateSelector,
  boardsSelector,
  campaignStateSelector
} from '@redux/selectors/tasks';
import {
  fetchAssetAssignees,
  fetchCampaignTaskBoards
} from '@redux/actions/tasks';
import CommentTree from '@pages/MediaViewer/Comments/CommentTree';
import { FabricContext } from '@context/FabricContext';
import { usePlayer } from '@context/PlayerContext';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import { useDeleteAttachment } from '@hooks/queries/attachment';
import { useMVCommentAttachUppy } from '@hooks/useMVCommentAttachUppy';
import {
  MediaViewerOutletContextState,
  UpdateAssetCommentParams
} from '@layouts/types';
import {
  buildCreateCommentPayload,
  buildEditCommentPayload,
  createTurnCommentIntoTask,
  mapUppyFilesToAttachments
} from '@pages/MediaViewer/helpers/comment';
import { useCommentMentions } from '@pages/MediaViewer/hooks/useComment';
import { FilterSortPanelComments } from '@pages/MediaViewer/components/FilterSortPanel';
import {
  DEFAULT_FILTER as COMMENTS_DEFAULT_FILTER,
  MY_COMMENT_FILTER
} from '@pages/MediaViewer/components/FilterSortPanel/FilterSortPanelComments';
import { useResponsive } from '@hooks/useResponsive';

function scrollToComment(commentId?: string) {
  if (!commentId) return;
  document
    .querySelector(
      `.media_viewer_comments_container [data-comment-id="${commentId}"]`
    )
    ?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
}

type CommentsProps = {
  selectedVersion: AssetVersionItemDto;
  asset: AssetItemDto;
  canCreateTask: boolean;
  commentMentions: boolean;
  commentsList: AssetVersionCommentDto[];
  isLoadingCommentsList: boolean;
  onClosePanel?: () => void;
  isPanelPinned?: boolean;
  onTogglePanelPin?: () => void;
  setSelectedCollapse?: (collapse: string | null) => void;
};

function Comments({
  canCreateTask,
  commentMentions,
  asset,
  selectedVersion,
  commentsList,
  isLoadingCommentsList,
  onClosePanel,
  isPanelPinned,
  onTogglePanelPin,
  setSelectedCollapse
}: CommentsProps) {
  const {
    addNewComment,
    removeComment,
    setCommentLike,
    setCommentResolve,
    editComment,
    getPotentialMentions,
    getPotentialGroupMentions,
    workspace
  } = useOutletContext<MediaViewerOutletContextState>();
  const { isSmWidth, isLgWidth, isMdWidth } = useResponsive();
  const { clearCanvas, canvas } = useContext(FabricContext);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEmbeddedWebView = searchParams.get('embedded') === '1';
  const dispatch = useTypedDispatch();
  const { mutateAsync: deleteAttachment } = useDeleteAttachment({});
  const { currentOrganization } = useOrganization(false);
  const createTask = useCreateTask();
  const hasAnnotations = useHasAnnotations();
  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const canLeaveComment = useAssetMVPermissions('comment');
  const canReadComment = useAssetMVPermissions('readComments');
  const canViewTasks = useAssetMVPermissions('listTasks');
  const { player } = usePlayer();
  const copiedComments = useTypedSelector(
    (state) => state.mediaViewer.copiedComments
  );
  const { mutateAsync: pasteAssetComments, isPending: isPastePending } =
    usePasteAssetCommentsMutation();

  const commentsListRef = useRef(commentsList);
  commentsListRef.current = commentsList;

  const assetId = asset.id;
  const uppy = useMVCommentAttachUppy(assetId);
  const {
    selectedCommentId,
    activePageNumber,
    canvasMode,
    playerTime,
    isCommentFieldFocused,
    commentText,
    documentHighlight,
    activeNewCommentEditor,
    shapesDrawingTools,
    setDrawToolsShape,
    selectComment,
    showReplyOfCommentId,
    setShowReplyOfCommentId,
    setActivePageNumber,
    setCanvasMode,
    setDocumentHighlight,
    setCommentText,
    newCommentTaskDraft,
    setNewCommentIsShowTask,
    setNewCommentSelectedUser,
    setNewCommentSelectedBoard,
    resetNewCommentTaskDraft,
    setIsCommentFieldFocused,
    setMobileSidebarCommentEditing,
    setActiveNewCommentEditor
  } = useAssetViewerState();
  const { campaignId } = asset;
  const { workspaceId } = asset;
  const assetType = selectedVersion?.metadata.type;
  const isAudioVideo = assetType === 'video' || assetType === 'audio';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const checkboxValueType =
    (isDocument && 'pageNumber') || (isAudioVideo && 'timeCode') || null;

  useFetch({
    key: `campaign-task-board-${campaignId}`,
    disabled: user?.type !== 'internal' || !campaignId || !canViewTasks,
    selector: (state) =>
      campaignStateSelector(state, { campaignId }).boards.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchCampaignTaskBoards({
          campaignId,
          fetchType
        })
      );
    }
  });
  useEffect(() => {
    dispatch(setListsNeedRefreshInCampaign({ campaignId }));
  }, [campaignId, dispatch]);

  useFetch({
    key: `asset-assignees-${assetId}`,
    disabled: user?.type !== 'internal' || !assetId || !canViewTasks,
    selector: (state) =>
      assetStateSelector(state, { assetId }).potentialAssignees.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchAssetAssignees({
          workspaceId,
          assetId,
          fetchType
        })
      );
    }
  });
  useEffect(() => {
    dispatch(setAssetListNeedRefresh({ assetId }));
  }, [assetId, dispatch]);

  const taskBoards = useTypedSelector((state) =>
    boardsSelector(state, { campaignId })
  ).filter((x) => x.id !== 'archived' && x.id !== 'no_assigned');
  const potentialAssignees = useTypedSelector(
    (state) => assetStateSelector(state, { assetId }).potentialAssignees.list
  );

  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(true);
  const [editCommentId, setEditCommentId] = useState<string | null>(null);
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [isUnresolvedComments, setIsUnresolvedComments] =
    useState<boolean>(false);
  const [sortSelected, setSortSelected] = useState<string>('');
  const [filterCommentsSelected, setFilterCommentsSelected] = useState<
    string[]
  >([COMMENTS_DEFAULT_FILTER]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isMouseOverComments, setIsMouseOverComments] = useState(false);
  const queryCommentId = searchParams.get('commentId');
  const selectedComment = useSelectedMVComment();

  const onCancelCommentDraft = useCallback(() => {
    setReplyToCommentId(null);
    setEditCommentId(null);
    setDrawToolsShape(DEFAULT_DRAW_TOOL);
    setCanvasMode('view');
    selectComment(null);
    setShowReplyOfCommentId(null);
    setDocumentHighlight(null);
    setCommentText(null);
    resetNewCommentTaskDraft();
    setIsCommentFieldFocused(false);
    setMobileSidebarCommentEditing(false);
    clearCanvas();
    uppy.cancelAll();
  }, [
    setDrawToolsShape,
    setCanvasMode,
    selectComment,
    setShowReplyOfCommentId,
    setDocumentHighlight,
    setCommentText,
    resetNewCommentTaskDraft,
    setIsCommentFieldFocused,
    setMobileSidebarCommentEditing,
    clearCanvas,
    uppy
  ]);

  // useOnClickOutside(commentsContainerRef, (e) => {
  //   if (
  //     e.target.closest('.float_comment_item') ||
  //     e.target.closest('.float_comment_editor')
  //   ) {
  //     return;
  //   }
  //   if (
  //     selectedComment?.timeCode?.toSeconds &&
  //     (e.target.closest('.video_control_bar_container') ||
  //       e.target.closest('.video_control_bar_track'))
  //   )
  //     return;
  //   if (selectedCommentId === 'new' || selectedCommentId === 'reply') {
  //     if (
  //       e.target.classList.contains('upper-canvas') ||
  //       e.target.closest(PANEL_OVERLAY_SELECTOR) ||
  //       e.target.closest('.media_viewer_pdf')
  //     ) {
  //       return;
  //     }
  //     const hasUnsavedDraft =
  //       !!commentText?.trim() ||
  //       hasAnnotations ||
  //       !!documentHighlight?.rects?.length;
  //     if (hasUnsavedDraft) return;
  //     onCancelCommentDraft();
  //     return;
  //   }
  //   if (
  //     canvasMode === 'view' &&
  //     !e.target.classList.contains('upper-canvas') &&
  //     !e.target.closest('.media_viewer_panel-action-btn')
  //   ) {
  //     setCanvasMode('view');
  //     selectComment(null);
  //     setDocumentHighlight(null);
  //   }
  // });

  const selectedCommentIdRef = useRef(selectedCommentId);
  selectedCommentIdRef.current = selectedCommentId;
  const isMdWidthRef = useRef(isMdWidth);
  isMdWidthRef.current = isMdWidth;

  useEffect(() => {
    return () => {
      const commentId = selectedCommentIdRef.current;
      if (commentId !== 'new' && commentId !== 'reply') return;
      if (isMdWidthRef.current) return;

      setReplyToCommentId(null);
      setEditCommentId(null);
      setDrawToolsShape(DEFAULT_DRAW_TOOL);
      setCanvasMode('view');
      selectComment(null);
      setShowReplyOfCommentId(null);
      setDocumentHighlight(null);
      setCommentText(null);
      resetNewCommentTaskDraft();
      setIsCommentFieldFocused(false);
      setMobileSidebarCommentEditing(false);
      uppy.cancelAll();
    };
  }, []);

  useEffect(() => {
    if (!isMdWidth || !editCommentId) return;
    if (
      selectedCommentId === editCommentId ||
      selectedCommentId === 'new' ||
      selectedCommentId === 'reply'
    ) {
      return;
    }
    setEditCommentId(null);
    setMobileSidebarCommentEditing(false);
  }, [
    selectedCommentId,
    editCommentId,
    isMdWidth,
    setMobileSidebarCommentEditing
  ]);

  const formattedComments = useCallback(() => {
    let list = formatCommentsTree(commentsList);
    if (list.length <= 0) return [];

    if (searchQuery) {
      list = list.filter(({ text }: { text: string }) =>
        text.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (isUnresolvedComments) {
      list = list.filter((el) => !el.resolvedAt);
    }

    if (
      filterCommentsSelected.length &&
      !filterCommentsSelected.includes(COMMENTS_DEFAULT_FILTER)
    ) {
      const isMyComments = filterCommentsSelected.includes(MY_COMMENT_FILTER);
      const isMatch = (item: TCommentItem | undefined) =>
        item &&
        (isMyComments
          ? item.user?.me
          : filterCommentsSelected.includes(item.user?.id || ''));
      list = list.reduce<TCommentItem[]>((acc, el) => {
        const existUser = isMatch(el);
        const filteredChildren = el.children?.filter(isMatch);
        if (existUser || filteredChildren?.length) {
          acc.push({
            ...el,
            children: filteredChildren
          });
        }
        return acc;
      }, []);
    }

    if (sortSelected === 'Priority')
      list = list.sort((x: any, y: any) =>
        x.priority === y.priority ? 0 : x ? -1 : 1
      );

    if (sortSelected === 'Date created')
      list = list.sort(
        (x: any, y: any) =>
          +moment(y.createdAt).format('X') - +moment(x.createdAt).format('X')
      );

    if (sortSelected === 'Date modified')
      list = list.sort(
        (x: any, y: any) =>
          (+moment(y.updatedAt).format('X') || 0) -
          (+moment(x.updatedAt).format('X') || 0)
      );

    if (sortSelected === 'Page') {
      list = list.sort((x: any, y: any) => {
        if (!!x.documentPage && !!y.documentPage)
          return x.documentPage - y.documentPage;
        if (!x.documentPage && !y.documentPage) return 0;
        return !x.documentPage ? 1 : -1;
      });
    }

    if (sortSelected === 'Timecode') {
      list = list.sort((x: any, y: any) => {
        const xTime = x.timeCode
          ? x.timeCode.fromSeconds
          : +moment(x.createdAt).format('X');
        const yTime = y.timeCode
          ? y.timeCode.fromSeconds
          : +moment(y.createdAt).format('X');

        return xTime - yTime;
      });
    }
    return list;
  }, [
    commentsList,
    isUnresolvedComments,
    filterCommentsSelected,
    sortSelected,
    searchQuery
  ])();

  const visibleCommentIds = useMemo(
    () =>
      formattedComments.flatMap((comment) => [
        comment.id,
        ...(comment.children?.map((child) => child.id) ?? [])
      ]),
    [formattedComments]
  );

  const visibleCommentsForExport = useMemo(
    () =>
      formattedComments.flatMap((comment) => [
        comment,
        ...(comment.children ?? [])
      ]),
    [formattedComments]
  );

  const onExport = (value: 'CSV' | 'XML' | 'Plain text') => {
    if (!workspace || !asset || !selectedVersion || !formattedComments.length) {
      return;
    }
    if (value === 'Plain text') {
      exportAsPlainText(selectedVersion, formattedComments);
    }
    if (value === 'CSV') {
      exportAsCSV(
        workspace.name,
        asset,
        selectedVersion,
        visibleCommentsForExport
      );
    }
    if (value === 'XML') {
      exportAsXML(workspace.name, asset, selectedVersion, formattedComments);
    }
  };

  const isInternalUser = user?.type === 'internal';
  const isPasteDisabled =
    isPastePending ||
    !copiedComments?.commentIds?.length ||
    copiedComments.assetVersionId === selectedVersion.id;
  const isCopyDisabled = !visibleCommentIds.length;
  const hasVisibleComments = formattedComments.length > 0;
  const commentsListLength = commentsList?.length;

  const dropdownMenuItems = useMemo<ItemType[]>(
    () => [
      {
        label: (
          <UpgradePlanTooltip
            text=" to download comments."
            isCanVisible={
              !currentOrganization?.entity?.features.downloadComments
            }
          >
            <span>Export comments as</span>
          </UpgradePlanTooltip>
        ),
        icon: (
          <DownloadInvoiceSvg
            width={24}
            height={24}
            className="b-icon-download"
          />
        ),
        expandIcon: currentOrganization?.entity?.features.downloadComments ? (
          <WhiteArrowSvg />
        ) : (
          <UpgradePlanSvg style={{ marginLeft: 4 }} />
        ),
        disabled:
          !currentOrganization?.entity?.features.downloadComments ||
          !hasVisibleComments,
        key: 'submenu',
        popupClassName: 'media_viewer_submenu media_viewer_mobile-overlay',
        popupOffset: [12, 0],
        onTitleClick: ({ domEvent }) => domEvent.stopPropagation(),
        children: [
          { label: 'CSV', key: 'CSV' },
          { label: 'XML', key: 'XML' },
          { label: 'Plain text', key: 'Plain text' }
        ]
      },
      ...(isInternalUser
        ? [
            {
              label: 'Copy comments',
              icon: <CopySvg width={24} height={24} className="b-icon-copy" />,
              key: 'copyComments',
              className: 'b-copy-comments-item',
              disabled: isCopyDisabled
            },
            {
              label: 'Paste comments',
              icon: <ClipboardSvg />,
              key: 'pasteComments',
              disabled: isPasteDisabled
            }
          ]
        : []),

      {
        type: 'divider'
      },
      {
        label:
          'Only visible comments, including filtered or searched results, will be copied or exported.',
        icon: <HelpCircleSvg />,
        key: 'onlyVisibleComments',
        className: 'b-info'
      }
    ],
    [
      currentOrganization,
      isCopyDisabled,
      isPasteDisabled,
      hasVisibleComments,
      isInternalUser
    ]
  );

  const onCopyComments = () => {
    if (!selectedVersion?.id || !visibleCommentIds.length) return;
    dispatch(
      setCopiedComments({
        assetVersionId: selectedVersion.id,
        commentIds: visibleCommentIds
      })
    );
    const messageKey = 'copy-comments-success';
    message.success({
      key: messageKey,
      className: 'message-dark-modal copy-success-toast',
      content: (
        <div className="message-dark-modal-content">
          <div>Comments copied to clipboard</div>
          <CrossSvg
            onClick={() => message.destroy(messageKey)}
            className="message-dark-modal-close-btn"
          />
        </div>
      ),
      icon: <SuccessSvg className="anticon" />
    });
  };

  const onPasteComments = async () => {
    if (isPasteDisabled || !copiedComments || !selectedVersion?.id) return;
    try {
      await pasteAssetComments({
        assetVersionId: selectedVersion.id,
        commentIds: copiedComments.commentIds
      });
      dispatch(setCopiedComments(null));
      const messageKey = 'paste-comments-success';
      message.success({
        key: messageKey,
        className: 'message-dark-modal copy-success-toast',
        content: (
          <div className="message-dark-modal-content">
            <div>Comments copied to clipboard</div>
            <CrossSvg
              onClick={() => message.destroy(messageKey)}
              className="message-dark-modal-close-btn"
            />
          </div>
        ),
        icon: <SuccessSvg className="anticon" />
      });
    } catch (error: any) {
      message.error({
        content: error?.response?.data?.message || 'Something went wrong',
        className: 'message-dark-modal'
      });
    }
  };

  const onDropdownMenuHandler = (key: string) => {
    if (key === 'copyComments') {
      onCopyComments();
      return;
    }
    if (key === 'pasteComments') {
      onPasteComments();
      return;
    }
    if (key === 'onlyVisibleComments') return;

    onExport(key as 'CSV' | 'XML' | 'Plain text');
  };

  useEffect(() => {
    if (!commentsList.length) return;
    if (!queryCommentId) return;
    const repliedCommentId = commentsList.find(
      (el) => el.id === queryCommentId
    )?.repliedToId;
    const commentPage = commentsList.find(
      (it) => it.id === queryCommentId
    )?.documentPage;
    if (commentPage) {
      setActivePageNumber(commentPage);
    }
    if (repliedCommentId) {
      setShowReplyOfCommentId(repliedCommentId);
      if (showReplyOfCommentId) scrollToComment(queryCommentId);
    } else {
      scrollToComment(queryCommentId);
    }
    searchParams.delete('commentId');
    setSearchParams(searchParams);
    selectComment(queryCommentId);
    setCanvasMode('view');
  }, [commentsList, queryCommentId]);

  useEffect(() => {
    if (
      selectedCommentId !== 'new' &&
      canvasMode !== 'edit' &&
      !(isMdWidth && editCommentId)
    ) {
      setEditCommentId(null);
    }
  }, [selectedCommentId, canvasMode, isMdWidth, editCommentId]);

  useEffect(() => {
    if (!isDocument) return;
    if (!activePageNumber) return;
    if (!commentsListRef.current?.length) return;
    if (sortSelected !== 'Page') return;

    let closestComment = null;
    let closestDiff = Infinity;
    const _commentsList = commentsListRef.current.filter((i) => !i.topParentId);

    for (const commentItem of _commentsList) {
      const documentPage = commentItem.documentPage || -1;
      const diff = Math.abs(documentPage - activePageNumber);
      if (diff === 0) {
        closestComment = commentItem;
        break;
      }
      if (diff < closestDiff) {
        closestDiff = diff;
        closestComment = commentItem;
      }
    }

    if (!closestComment?.id) return;
    scrollToComment(closestComment?.id);
  }, [isDocument, activePageNumber, sortSelected]);

  useEffect(() => {
    if (!isAudioVideo || !player) return;
    const onTimeUpdate = () => {
      if (isMouseOverComments || sortSelected !== 'Timecode') return;
      if (!commentsListRef.current?.length) return;
      const currentTime = player.currentTime();
      const closestComment = commentsListRef.current
        .filter((i) => !i.topParentId)
        .reduce<AssetVersionCommentDto | null>((closest, current) => {
          const time = current.timeCode ? current.timeCode.fromSeconds : null;
          const closestTime = closest?.timeCode
            ? closest.timeCode.fromSeconds
            : null;
          if (time !== null) {
            const diff = Math.abs(time - currentTime);
            const closestDiff =
              closestTime !== null
                ? Math.abs(closestTime - currentTime)
                : Infinity;
            return diff < closestDiff ? current : closest;
          }
          return closest;
        }, null);
      if (closestComment?.id) scrollToComment(closestComment.id);
    };
    player.on('timeupdate', onTimeUpdate);
    return () => player.off('timeupdate', onTimeUpdate);
  }, [isAudioVideo, isMouseOverComments, player, sortSelected]);

  useLayoutEffect(() => {
    setFilterCommentsSelected([COMMENTS_DEFAULT_FILTER]);
    setIsUnresolvedComments(false);
  }, [selectedVersion?.id, setIsUnresolvedComments, setFilterCommentsSelected]);

  useEffect(() => {
    if (activeNewCommentEditor === 'sidebar') {
      if (editCommentId !== 'new') setEditCommentId('new');
      return;
    }
    if (editCommentId === 'new') {
      setEditCommentId(null);
    }
  }, [activeNewCommentEditor, editCommentId]);

  useEffect(() => {
    return () => {
      setActiveNewCommentEditor('float');
    };
  }, [setActiveNewCommentEditor]);

  useEffect(() => {
    const selectedComment = commentsList.find(
      (it) => selectedCommentId === it.id
    );
    const replyParentId =
      selectedComment?.topParentId ||
      (selectedComment?.repliedToId
        ? commentsList.find((it) => it.id === selectedComment.repliedToId)
            ?.topParentId || selectedComment.repliedToId
        : null);
    if (replyParentId) setShowReplyOfCommentId(replyParentId);
    if (
      selectedCommentId &&
      selectedCommentId !== 'new' &&
      selectedCommentId !== 'reply'
    ) {
      requestAnimationFrame(() => scrollToComment(selectedCommentId));
    }
  }, [selectedCommentId, commentsList, setShowReplyOfCommentId]);

  const onTurnCommentIntoTask = createTurnCommentIntoTask({
    asset,
    user,
    createTask,
    dispatch,
    selectedBoardId: newCommentTaskDraft.selectedBoard?.id,
    selectedUser: newCommentTaskDraft.selectedUser,
    isOpenTaskModal: !isSmWidth,
    setSelectedCollapse,
    openTasksInSidebar: isLgWidth
  });

  const onAddComment = async (values: SubmitEditorValues) => {
    const parentId = values.idOrParentId;
    const parentComment = commentsList.find((it) => it.id === parentId);
    const data = buildCreateCommentPayload({
      values,
      selectedVersionId: selectedVersion?.id || '',
      isCheckboxChecked,
      isAudioVideo,
      isDocument,
      playerTime,
      activePageNumber,
      annotations: getAnnotations(),
      parentComment
    });
    const comment = await addNewComment(data, parentId);
    const files = uppy.getFiles();
    if (files.length) {
      files.forEach((item) => {
        uppy.setFileMeta(item.id, {
          commentId: comment.id,
          readyForUpload: true
        });
      });
      await uppy.upload();
    }
    await createNewComment(
      {
        ...comment,
        attachments: mapUppyFilesToAttachments(files)
      },
      selectedVersion?.id || ''
    );
    clearCanvas();
    setDocumentHighlight(null);
    requestAnimationFrame(() => {
      scrollToComment(comment.id);
    });

    const keepToolActive =
      !isMdWidth && isAnnotationDrawTool(shapesDrawingTools.shape);
    if (keepToolActive) {
      selectComment('new');
      setCanvasMode('edit');
    } else {
      setDrawToolsShape(DEFAULT_DRAW_TOOL);
      setCanvasMode('view');
      selectComment(null);
    }

    if (newCommentTaskDraft.isShowTask) {
      onTurnCommentIntoTask(comment);
    }
    resetNewCommentTaskDraft();
  };

  const onDeleteComment = useCallback(
    async (id: string) => {
      await removeComment(id);
    },
    [removeComment]
  );

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (
        e.key === 'Delete' &&
        selectedComment?.id &&
        canvasMode === 'view' &&
        selectedComment.user?.me
      ) {
        setCanvasMode('view');
        selectComment(null);
        onDeleteComment(selectedComment.id);
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [
    dispatch,
    canvasMode,
    onDeleteComment,
    selectedComment?.id,
    selectedComment?.user?.me
  ]);

  const onToggleLikeComment = async ({
    id,
    value
  }: {
    id: string;
    value: boolean;
  }) => {
    const {
      id: userId,
      type,
      pictureUrl,
      email,
      firstName,
      lastName
    } = nonNull(user);
    await setCommentLike(
      { id, value },
      {
        id: userId,
        type: type === 'internal' ? 'user' : 'external-reviewer',
        picture: { url: pictureUrl ?? null },
        email,
        firstName,
        lastName: lastName || '',
        me: true
      }
    );
  };

  const onToggleResolveComment = async ({
    id,
    value
  }: {
    id: string;
    value: boolean;
  }) => {
    const {
      id: userId,
      type,
      pictureUrl,
      email,
      firstName,
      lastName
    } = nonNull(user);
    await setCommentResolve({ id, value } as any, {
      id: userId,
      type: type === 'internal' ? 'user' : 'external-reviewer',
      picture: { url: pictureUrl ?? null },
      email,
      firstName,
      lastName: lastName || '',
      me: true
    });
  };

  const onEditComment = async ({
    text,
    rawText,
    mentions,
    groupMentions,
    idOrParentId: id,
    documentHighlight,
    documentHighlightRects,
    documentHighlightReplacement,
    attachmentsLeft
  }: SubmitEditorValues) => {
    const comment = commentsList.find((it) => it.id === id);
    if (!comment) return;
    const data: UpdateAssetCommentParams = {
      ...buildEditCommentPayload(
        {
          text,
          rawText,
          mentions,
          groupMentions,
          idOrParentId: id,
          documentHighlight,
          documentHighlightRects,
          documentHighlightReplacement,
          attachmentsLeft
        },
        comment
      ),
      annotations: getAnnotations(),
      timeCode: isAudioVideo
        ? isCheckboxChecked
          ? {
              fromSeconds: playerTime[0] || 0,
              toSeconds: playerTime[1]
            }
          : null
        : undefined
    };
    const files = uppy.getFiles();
    const attachmentIdsLeft = (attachmentsLeft || []).map((item) => item.id);
    const removeFilesPromise = (comment?.attachments || [])
      .filter((attachment) => !attachmentIdsLeft.includes(attachment.id))
      .map((attachment) =>
        deleteAttachment({
          id: attachment.id
        })
      );
    files.forEach((item) => {
      uppy.setFileMeta(item.id, {
        commentId: comment.id,
        readyForUpload: true
      });
    });
    if (removeFilesPromise.length) await Promise.all(removeFilesPromise);
    if (files.length) await uppy.upload();
    await editComment(data);
    clearCanvas();
    setDrawToolsShape(DEFAULT_DRAW_TOOL);
    setDocumentHighlight(null);
    setCanvasMode('view');
  };

  const getAnnotations = (): string | null => {
    if (!canvas) return null;
    return getFloatCanvasAnnotations(canvas);
  };

  const {
    potentialMentions,
    potentialGroupMentions,
    setSearchQuery: setMentionSearch
  } = useCommentMentions({
    assetVersionId: selectedVersion?.id,
    getPotentialMentions,
    getPotentialGroupMentions
  });

  const canExternalMakeAction = (callBack: any) => {
    if (!user) {
      dispatch(
        toggleSingInExternalReviewerModal({
          visible: true
        })
      );
      return;
    }
    callBack();
  };

  return (
    <PanelSection
      name="Comments"
      dropdownMenu={dropdownMenuItems}
      onDropdownHandler={onDropdownMenuHandler}
      count={commentsListLength}
      onClose={onClosePanel}
      isPinned={isPanelPinned}
      onTogglePin={onTogglePanelPin}
    >
      <>
        {canReadComment && (
          <FilterSortPanelComments
            commentsList={commentsList}
            onSortHandler={setSortSelected}
            dropdownValue={filterCommentsSelected}
            onDropdownHandler={setFilterCommentsSelected}
            onSearchHandler={(v) => setSearchQuery(v)}
            isUnresolved={isUnresolvedComments}
            onUnResolveHandler={setIsUnresolvedComments}
            assetType={
              isAudioVideo ? 'audio' : isDocument ? 'document' : undefined
            }
          />
        )}

        <div
          ref={commentsContainerRef}
          onMouseEnter={() => setIsMouseOverComments(true)}
          onMouseLeave={() => setIsMouseOverComments(false)}
          className="media_viewer_comments_container"
        >
          <div
            className={classNames('media_viewer_comments_top', {
              'media_viewer_comments_top-draft': editCommentId === 'new'
            })}
          >
            {canLeaveComment && (
              <CommentEditor
                commentMentions={commentMentions}
                editCommentId={editCommentId}
                setEditCommentId={setEditCommentId}
                editorFor="new"
                autoFocus={false}
                isFocused={editCommentId === 'new'}
                replyToCommentId={replyToCommentId}
                setReplyToCommentId={setReplyToCommentId}
                onSubmitEditor={onAddComment}
                isCheckboxChecked={isCheckboxChecked}
                setIsCheckboxChecked={setIsCheckboxChecked}
                checkboxValueType={checkboxValueType}
                potentialMentions={potentialMentions}
                potentialGroupMentions={potentialGroupMentions}
                searchPotentialMentions={(v) => setMentionSearch(v)}
                uppy={uppy}
                isSidebarNewCommentEditor
                isEmbeddedWebView={isEmbeddedWebView}
              />
            )}
            {editCommentId === 'new' && (
              <CommentTask
                selectedUser={newCommentTaskDraft.selectedUser}
                setSelectedUser={setNewCommentSelectedUser}
                selectedBoard={newCommentTaskDraft.selectedBoard}
                setSelectedBoard={setNewCommentSelectedBoard}
                isShowTask={newCommentTaskDraft.isShowTask}
                setIsShowTask={setNewCommentIsShowTask}
                potentialAssignees={potentialAssignees}
                boardList={
                  selectedVersion?.versionNumber === asset?.asset?.versionsCount
                    ? taskBoards
                    : []
                }
                canCreateTask={canCreateTask}
                isEmbeddedWebView={isEmbeddedWebView}
                leftSlot={
                  <div
                    className="media_viewer_comment_task_cancel"
                    onClick={onCancelCommentDraft}
                  >
                    Cancel
                  </div>
                }
                rightSlot={
                  isCommentFieldFocused && isMdWidth ? (
                    <KeyboardHideSvg style={{ display: 'block' }} />
                  ) : undefined
                }
              />
            )}
          </div>
          {isLoadingCommentsList && (
            <List
              className="media_viewer_comments_list-skeleton"
              itemLayout="horizontal"
              bordered={false}
              split={false}
              dataSource={new Array(10)
                .fill(undefined)
                .map((_, i) => ({ key: i }))}
              renderItem={() => (
                <List.Item>
                  <Skeleton.Input style={{ height: 88 }} active />
                </List.Item>
              )}
            />
          )}

          {!isLoadingCommentsList && (
            <List
              className={classNames(
                'media_viewer_comments_list',
                !formattedComments.length && 'media_viewer_comments_list-empty'
              )}
              dataSource={formattedComments}
              rowKey="id"
              itemLayout="horizontal"
              renderItem={(comment) => (
                <CommentTree
                  commentMentions={commentMentions}
                  comment={comment as any}
                  editCommentId={editCommentId}
                  selectedCommentId={selectedCommentId}
                  replyToCommentId={replyToCommentId}
                  showReplyOfCommentId={showReplyOfCommentId}
                  isCheckboxChecked={isCheckboxChecked}
                  checkboxValueType={checkboxValueType}
                  onReply={(comment) => {
                    canExternalMakeAction(() => {
                      setEditCommentId(null);
                      setMobileSidebarCommentEditing(false);
                      setDocumentHighlight(null);
                      if (replyToCommentId === comment.id) {
                        setCanvasMode('view');
                        setReplyToCommentId(null);
                        selectComment(null);
                      } else {
                        setReplyToCommentId(comment.id);
                        setShowReplyOfCommentId(
                          comment.topParentId || comment.id
                        );
                        setCanvasMode('edit');
                        selectComment('reply');
                      }
                    });
                  }}
                  onLikeClick={(comment) => {
                    canExternalMakeAction(() => {
                      const hasLike = comment.likes.find((el: any) => el.me);
                      onToggleLikeComment({ value: !hasLike, id: comment.id });
                    });
                  }}
                  onResolveClick={(comment) => {
                    canExternalMakeAction(() => {
                      onToggleResolveComment({
                        value: !comment.resolvedAt,
                        id: comment.id
                      });
                    });
                  }}
                  onShowReplyClick={(comment) => {
                    if (showReplyOfCommentId === comment.id)
                      setShowReplyOfCommentId(null);
                    else setShowReplyOfCommentId(comment.id);
                    setReplyToCommentId(null);
                  }}
                  potentialMentions={potentialMentions}
                  potentialGroupMentions={potentialGroupMentions}
                  searchPotentialMentions={(v) => setMentionSearch(v)}
                  onAddComment={onAddComment}
                  setEditCommentId={setEditCommentId}
                  setReplyToCommentId={setReplyToCommentId}
                  setIsCheckboxChecked={setIsCheckboxChecked}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                  onTurnCommentIntoTask={onTurnCommentIntoTask}
                  canCreateTask={canCreateTask}
                  isEmbeddedWebView={isEmbeddedWebView}
                  uppy={uppy}
                />
              )}
              locale={{
                emptyText: !canReadComment ? (
                  <div className="media_viewer_comments_empty">
                    <CommentCloseSvg className="b-comment-icon" />
                    You have view-only permission
                  </div>
                ) : searchQuery && !formattedComments.length ? (
                  <div className="media_viewer_comments_empty">
                    <EmptySearchSvg className="b-comment-icon" />
                    <span className="title">No results found</span>
                    It seems we can’t find any results based on your search
                  </div>
                ) : (
                  <div className="media_viewer_comments_empty">
                    <CommentSvg className="b-comment-icon" />
                    No comments yet
                  </div>
                )
              }}
            />
          )}
        </div>
      </>
    </PanelSection>
  );
}

export default Comments;
